import { marked } from 'marked';
import DOMPurify from 'dompurify';

// Shared markdown → safe-HTML pipeline for Contentful / Learning Center content.
//
// SECURITY: `content` can be authored by non-engineers (CST Learning Center modules), so the
// HTML produced by `marked` is sanitized with DOMPurify before it is handed to a renderer —
// this strips <script>, inline event handlers, javascript: URLs, etc. Never render marked()
// output raw; always route it through here.

// Harden anchors: links that leave the app open in a new tab and can't reach window.opener.
// Same-document fragment links (#section) stay in-page so "[Back to top](#…)" and TOC-style
// cross-references scroll instead of spawning a duplicate tab; same-origin links (absolute or
// relative, e.g. an in-app "/privacy" link in the User Agreement) also navigate in place. Only
// genuinely external links are forced to a new tab.
// Registered once at module load (DOMPurify hooks are global and would otherwise stack).
DOMPurify.addHook('afterSanitizeAttributes', node => {
  if (node.tagName !== 'A') return;
  const href = node.getAttribute('href');
  if (!href || href.startsWith('#')) return;
  let isExternal = false;
  try {
    isExternal = new URL(href, window.location.href).origin !== window.location.origin;
  } catch {
    isExternal = false; // unparseable/relative-without-base — leave it in-tab
  }
  if (isExternal) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

export interface MarkdownHeading {
  id: string;
  text: string;
  level: number; // 1, 2, or 3
}

export interface RenderedMarkdown {
  html: string;
  headings: MarkdownHeading[];
}

/** Slugify heading text into a stable, URL-safe anchor id. */
function slugify(text: string): string {
  const base = text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return base || 'section';
}

/**
 * Render markdown to sanitized HTML and extract its heading outline.
 *
 * Headings (h1–h3) are given stable slug ids so a table of contents can link to them; the id
 * is injected after sanitization (ids are inert), so it never widens the attack surface.
 */
export function renderMarkdown(content: string | null | undefined): RenderedMarkdown {
  const source = content ?? '';
  if (!source.trim()) return { html: '', headings: [] };

  const rawHtml = DOMPurify.sanitize(marked(source) as string);

  // DOMParser is browser-only; this app is a client-rendered SPA, but guard anyway so a
  // non-DOM environment (tests/SSR) still gets usable HTML instead of throwing.
  if (typeof DOMParser === 'undefined') return { html: rawHtml, headings: [] };

  const doc = new DOMParser().parseFromString(rawHtml, 'text/html');
  const headings: MarkdownHeading[] = [];
  const seen = new Map<string, number>();

  doc.querySelectorAll('h1, h2, h3').forEach(el => {
    const level = Number(el.tagName.charAt(1));
    const text = (el.textContent ?? '').trim();
    const base = slugify(text);
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    const id = count === 0 ? base : `${base}-${count}`;
    el.id = id;
    headings.push({ id, text, level });
  });

  return { html: doc.body.innerHTML, headings };
}
